import React, { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';

interface MarqueeTextProps {
  text: string;
  className?: string;
  style?: CSSProperties;
  /** px per second */
  speed?: number;
  /** ms paused at start/end */
  pauseDuration?: number;
}

/**
 * MarqueeText — single-line scrolling text za DOM (PWA + WebView).
 *
 * - Container ima `overflow:hidden` i fiksnu visinu (jedna linija).
 * - Mjeri širinu containera + teksta (ResizeObserver) i renderuje pomak SAMO
 *   ako tekst overflow-uje. U suprotnom je statičan i centriran.
 * - Animacija: requestAnimationFrame petlja (smooth, useNativeDriver-equivalent
 *   na webu), pauzira se na početku i kraju za `pauseDuration` ms, pa se vraća
 *   na 0 i ponavlja.
 * - Reset i remeasure na svaku promjenu `text` (nova pjesma → reset scroll).
 */
export const MarqueeText: React.FC<MarqueeTextProps> = ({
  text,
  className = '',
  style,
  speed = 50,
  pauseDuration = 1200,
}) => {
  // Koristimo <span> umjesto <div> da je MarqueeText portable i unutar <p>
  // (validateDOMNesting: <div> ne smije unutar <p>). Ručno postavljamo display.
  const containerRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const innerRef = useRef<HTMLSpanElement>(null);
  const rafRef = useRef<number | null>(null);
  const stateRef = useRef<{ phase: 'wait-start' | 'scroll' | 'wait-end'; t0: number; x: number }>(
    { phase: 'wait-start', t0: 0, x: 0 }
  );

  const [overflow, setOverflow] = useState<{ container: number; content: number }>(
    { container: 0, content: 0 }
  );

  useLayoutEffect(() => {
    const c = containerRef.current;
    const t = textRef.current;
    if (!c || !t) return;
    const measure = (): void => {
      setOverflow({ container: c.offsetWidth, content: t.offsetWidth });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(c);
    ro.observe(t);
    return () => ro.disconnect();
  }, [text]);

  useEffect(() => {
    const inner = innerRef.current;
    if (!inner) return;
    const distance = overflow.content - overflow.container;
    if (distance <= 0 || overflow.container === 0) {
      inner.style.transform = 'translateX(0px)';
      stateRef.current = { phase: 'wait-start', t0: 0, x: 0 };
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    const totalDistance = distance + 24;
    const scrollDurationMs = (totalDistance / speed) * 1000;
    stateRef.current = { phase: 'wait-start', t0: performance.now(), x: 0 };

    const tick = (now: number): void => {
      const s = stateRef.current;
      const elapsed = now - s.t0;
      switch (s.phase) {
        case 'wait-start': {
          if (elapsed >= pauseDuration) {
            s.phase = 'scroll';
            s.t0 = now;
          }
          break;
        }
        case 'scroll': {
          const p = Math.min(1, elapsed / scrollDurationMs);
          s.x = -totalDistance * p;
          inner.style.transform = `translateX(${s.x}px)`;
          if (p >= 1) {
            s.phase = 'wait-end';
            s.t0 = now;
          }
          break;
        }
        case 'wait-end': {
          if (elapsed >= pauseDuration) {
            s.phase = 'wait-start';
            s.t0 = now;
            s.x = 0;
            inner.style.transform = 'translateX(0px)';
          }
          break;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [overflow, speed, pauseDuration, text]);

  const distance = overflow.content - overflow.container;
  const isOverflow = distance > 0 && overflow.container > 0;

  return (
    <span
      ref={containerRef}
      className={`overflow-hidden ${isOverflow ? 'text-left' : 'text-center'} w-full`}
      style={{ display: 'block', ...style }}
    >
      <span ref={innerRef} className="whitespace-nowrap will-change-transform" style={{ display: 'inline-block' }}>
        <span ref={textRef} className={className} style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
          {text}
        </span>
      </span>
    </span>
  );
};
